import { PORTAL_CSRF_HEADER, isSafeMethod, readPortalCsrfToken, withCsrfHeader } from '../csrf';

export const API_BASE = '/api';

/**
 * Übersetzung eines Sprachmodell-Fehlers in Ursache, Auto-Lösung und Handgriffe.
 * Gebaut in `backend/services/llm/errorDiagnosis.ts`, gerendert von `LlmErrorAlert`.
 */
export interface LlmErrorDiagnosis {
  code: string;
  title: string;
  cause: string;
  owner: 'PORTAL' | 'ADMIN' | 'ANBIETER' | 'UNBEKANNT';
  autoFix: string | null;
  manualSteps: string[];
  retryWorthwhile: boolean;
  /** Der Wortlaut des Anbieters — unverkürzt, wird nie durch die Übersetzung ersetzt. */
  providerMessages: string[];
  httpStatus: number | null;
}

export interface ApiErrorBody {
  error: string;
  /** Feld-Fehler (Zod-Form) ODER einfache Fehlerliste (z. B. Finalize-Validierung). */
  details?: Record<string, string[]> | string[];
  /** Kurztext zur Ursache (z. B. API-Antwort) */
  detail?: string;
  /** Maschinenlesbare Fallkennung (z. B. 'TICKET_TRASHED') für eigene Darstellungen. */
  code?: string;
  /** Nur bei Fehlern des Sprachmodells, siehe `LlmErrorDiagnosis`. */
  diagnosis?: LlmErrorDiagnosis;
}

/**
 * Fehler mit getrennt verfügbarem technischem Detail.
 *
 * `message` bleibt der bisherige zusammengesetzte Text (rückwärtskompatibel für alle
 * Toasts), zusätzlich stehen `summary` (lesbare Handlungsanweisung) und `detail` (technischer
 * Originalwortlaut, z. B. die rohe Weclapp-Validierungsmeldung) einzeln bereit. Komponenten
 * können damit beides getrennt darstellen — die Anweisung prominent, den Wortlaut daneben.
 * Letzterer wird für die Fehlersuche gebraucht und darf nicht verloren gehen.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public summary: string,
    public detail?: string,
    /** Fallkennung aus dem Fehler-Body — erlaubt Sonderbehandlung ohne Text-Matching. */
    public code?: string,
    /** HTTP-Status der Antwort. */
    public status?: number,
    /**
     * Übersetzter Sprachmodell-Fehler. Nur gesetzt, wenn der Server ihn erkannt hat —
     * `message` und `detail` bleiben daneben unverändert stehen.
     */
    public diagnosis?: LlmErrorDiagnosis,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiValidationError extends Error {
  constructor(message: string, public fieldErrors: Record<string, string[]>) {
    super(message);
    this.name = 'ApiValidationError';
  }
}

/**
 * Server gerade nicht erreichbar — Deploy/Neustart (502/503/504, Cloudflare 52x) oder Netzfehler.
 * Sagt nichts über den Inhalt der Anfrage: Anmeldung, Entwurf und Warteschlange bleiben, wie sie
 * sind, und es wird später erneut versucht. Der Service Worker beantwortet GET-Anfragen ohne Netz
 * mit einer eigenen 503 — die fällt ebenfalls hierunter.
 */
export function isServerUnavailableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  if ((error as { networkError?: unknown }).networkError === true) return true;
  const status = (error as { status?: unknown }).status;
  if (typeof status !== 'number') return false;
  return status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 530);
}

/**
 * Netzfehler eines fetch kenntlich machen (isServerUnavailableError) — für apiRequest und das
 * Kundenportal (customerRequest). Abbrüche durch den Aufrufer (AbortError) bleiben, was sie sind.
 */
export function markNetworkError(err: unknown): unknown {
  if (err instanceof Error && err.name !== 'AbortError') Object.assign(err, { networkError: true });
  return err;
}

export interface ApiRequestOptions extends RequestInit {
  /**
   * Die Seite zeigt Modell, Tokens und Kosten des KIAN-Aufrufs selbst an — dann keinen
   * zusätzlichen allgemeinen KIAN-Hinweis einblenden (sonst stünde alles doppelt da).
   */
  kianUsageInline?: boolean;
}

export type RequestFn = <T>(
  endpoint: string,
  options?: ApiRequestOptions
) => Promise<T>;

/** Event für den allgemeinen KIAN-Hinweis (Listener in main.tsx). */
export const KIAN_USAGE_EVENT = 'portal:kian-usage';

/**
 * Jede Antwort, in der KIAN lief, trägt `kianUsage` (vom Server angehängt). Ohne eigene
 * Anzeige auf der Seite meldet ein allgemeiner Hinweis Modell, Tokens und Kosten — so
 * steht es bei JEDER KIAN-Funktion dabei, auch bei einer, die niemand eigens verdrahtet hat.
 */
function announceKianUsage(parsed: unknown, endpoint: string): void {
  if (typeof window === 'undefined' || !parsed || typeof parsed !== 'object') return;
  const usage = (parsed as { kianUsage?: unknown }).kianUsage;
  if (!usage) return;
  window.dispatchEvent(new CustomEvent(KIAN_USAGE_EVENT, { detail: { endpoint, usage } }));
}

const HTML_ERROR_HINT =
  'Die API-Antwort war keine JSON-Fehlermeldung, sondern eine HTML-Seite (häufig Proxy, Cloudflare oder Routing). Bitte erneut versuchen. Dauerhaft: prüfen, ob Anfragen an /api beim Backend ankommen (z. B. Coolify, nur Frontend exponiert).';

function htmlProxyErrorMessage(status: number, statusText: string): string {
  const st = statusText?.trim();
  return `HTTP ${status}${st ? ` ${st}` : ''}: ${HTML_ERROR_HINT}`;
}

function isProbablyHtmlBody(raw: string, contentType: string | null): boolean {
  const ct = (contentType ?? '').toLowerCase();
  if (ct.includes('text/html')) return true;
  const s = raw.trimStart().slice(0, 64).toLowerCase();
  return s.startsWith('<!doctype') || s.startsWith('<html');
}

function parseErrorMessage(
  status: number,
  statusText: string,
  raw: string,
  contentType: string | null
): string {
  if (!raw.trim()) return `HTTP ${status}${statusText?.trim() ? ` ${statusText.trim()}` : ''}`;
  if (isProbablyHtmlBody(raw, contentType)) return htmlProxyErrorMessage(status, statusText);
  try {
    const parsed = JSON.parse(raw) as ApiErrorBody;
    if (parsed.error) {
      // Finalize & Co. liefern details als String-Liste ("Arbeitszeit Start erforderlich", …).
      // Ohne diese Auflistung sah der Techniker nur ein nacktes "Validierungsfehler"
      // und konnte nicht wissen, was fehlt.
      if (Array.isArray(parsed.details)) {
        const listParts = parsed.details.filter((d): d is string => typeof d === 'string').join('; ');
        if (listParts) return `${parsed.error}: ${listParts}`;
      }
      if (parsed.details && typeof parsed.details === 'object' && !Array.isArray(parsed.details)) {
        const fieldParts = Object.entries(parsed.details)
          .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
          .join('; ');
        if (fieldParts) return `${parsed.error}: ${fieldParts}`;
      }
      const extra =
        typeof parsed.detail === 'string' && parsed.detail.trim() !== ''
          ? ` — ${parsed.detail.trim()}`
          : '';
      return `${parsed.error}${extra}`;
    }
  } catch {
    /* plain text or other */
  }
  const t = raw.trim();
  return t.length > 200 ? `${t.slice(0, 200)}…` : t;
}

/**
 * Führt die Anfrage aus und ergänzt für schreibende Methoden den CSRF-Header
 * (`X-CSRF-Token`, Wert aus dem Cookie `portal_csrf`).
 *
 * Der einmalige Wiederholungsversuch deckt zwei Fälle ab, in denen der Token
 * beim ersten Versuch zwangsläufig fehlt oder veraltet ist: eine Anmeldung, die
 * älter ist als dieses Feature (das Cookie kommt erst mit der Antwort), und ein
 * Cookie aus einer abgelaufenen Sitzung. Der Server legt der 403 ein frisches
 * Cookie bei — der zweite Versuch trägt damit den richtigen Wert. Wiederholt
 * wird nur bei `CSRF_TOKEN_INVALID` und nur, wenn sich der Token tatsächlich
 * geändert hat: sonst liefe eine dauerhafte Ablehnung in eine Schleife.
 */
async function sendWithCsrf(endpoint: string, options: RequestInit): Promise<Response> {
  const send = async (): Promise<{ response: Response; sentToken: string | null }> => {
    const headers: Record<string, string> = withCsrfHeader(options.method, {
      ...(options.headers as Record<string, string>),
    });
    if (options.body && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    let response: Response;
    try {
      response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
        credentials: 'include',
      });
    } catch (err) {
      throw markNetworkError(err);
    }
    return { response, sentToken: headers[PORTAL_CSRF_HEADER] ?? null };
  };

  const first = await send();
  if (first.response.status !== 403 || isSafeMethod(options.method)) return first.response;

  // Über einen Klon lesen — die Original-Response muss unangetastet bleiben,
  // damit der Aufrufer die Fehlermeldung unverändert auswerten kann.
  const raw = await first.response.clone().text();
  let code: string | undefined;
  try {
    code = (JSON.parse(raw) as { code?: string }).code;
  } catch {
    /* kein JSON-Body */
  }
  if (code !== 'CSRF_TOKEN_INVALID') return first.response;
  if (readPortalCsrfToken() === first.sentToken) return first.response;

  // ReadableStream-Bodies sind einmalig — die lassen sich nicht erneut senden
  // und laufen in die normale Fehlerbehandlung. FormData/Blob dagegen schon:
  // ein Foto-Upload wird lieber einmal wiederholt als verworfen.
  if (options.body instanceof ReadableStream) return first.response;
  return (await send()).response;
}

export async function apiRequest<T>(
  endpoint: string,
  { kianUsageInline, ...options }: ApiRequestOptions = {}
): Promise<T> {
  const response = await sendWithCsrf(endpoint, options);

  const raw = await response.text();
  const contentType = response.headers.get('content-type');

  if (!response.ok) {
    const message = parseErrorMessage(
      response.status,
      response.statusText,
      raw,
      contentType
    );

    // `/auth/me` beim Start: nur Store leeren (checkAuth), kein „Sitzung beendet”-Hinweis
    if (
      response.status === 401 &&
      typeof window !== 'undefined' &&
      endpoint !== '/auth/me' &&
      !endpoint.startsWith('/auth/login') &&
      !endpoint.startsWith('/auth/login-options') &&
      !endpoint.startsWith('/auth/magic-link/request')
    ) {
      window.dispatchEvent(new CustomEvent('portal:session-invalid'));
    }

    let structured: {
      summary: string;
      detail?: string;
      code?: string;
      diagnosis?: LlmErrorDiagnosis;
    } | null = null;
    try {
      const parsed = JSON.parse(raw) as ApiErrorBody;
      if (parsed.details && typeof parsed.details === 'object' && !Array.isArray(parsed.details)) {
        throw Object.assign(new ApiValidationError(message, parsed.details), { status: response.status });
      }
      if (parsed.error) {
        structured = {
          summary: parsed.error,
          detail:
            typeof parsed.detail === 'string' && parsed.detail.trim() !== ''
              ? parsed.detail.trim()
              : undefined,
          code: typeof parsed.code === 'string' ? parsed.code : undefined,
          diagnosis:
            parsed.diagnosis && typeof parsed.diagnosis === 'object' ? parsed.diagnosis : undefined,
        };
      }
    } catch (e) {
      if (e instanceof ApiValidationError) throw e;
    }

    // Mit strukturiertem Body einen ApiError werfen, damit `detail` getrennt verfügbar bleibt.
    if (structured) {
      throw new ApiError(
        message,
        structured.summary,
        structured.detail,
        structured.code,
        response.status,
        structured.diagnosis,
      );
    }
    // Status am Fehler: isServerUnavailableError trennt so Serverausfall von Ablehnung.
    throw Object.assign(new Error(message), { status: response.status });
  }

  if (response.status === 204 || raw.length === 0) {
    return {} as T;
  }

  let parsed: T;
  try {
    parsed = JSON.parse(raw) as T;
  } catch {
    if (isProbablyHtmlBody(raw, contentType)) {
      throw new Error(htmlProxyErrorMessage(response.status, response.statusText));
    }
    const preview = raw.trim().slice(0, 160);
    throw new Error(
      `Ungültige JSON-Antwort (HTTP ${response.status}). ${preview}${raw.length > 160 ? '…' : ''}`
    );
  }
  // Nur für Aktionen: Ein GET, in dem KIAN mitläuft (Liste lädt, Einstufung im Hintergrund),
  // hat niemand angeklickt — dort zeigt die Seite die Zahlen selbst, statt Meldungen aufpoppen zu lassen.
  if (!kianUsageInline && !isSafeMethod(options.method)) announceKianUsage(parsed, endpoint);
  return parsed;
}
